import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { documentRequestService, getErrorMessage } from '../../services/apiService';

const PublicDocumentRequest = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing link');
      setLoading(false);
      return;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await documentRequestService.getByToken(token);
      if (!response.success) throw new Error('Failed to load');
      setData(response.data);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to load document request. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (key, file) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
  };

  const handleSubmit = async () => {
    const hasFile = Object.values(files).some((f) => f && f.size > 0);
    if (!hasFile) {
      setError('Please upload at least one document');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const formData = new FormData();
      Object.entries(files).forEach(([key, file]) => {
        if (file && file.size > 0) {
          formData.append(key, file);
        }
      });

      await documentRequestService.submit(token, formData);
      setSubmitted(true);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to upload documents. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Error</h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <p className="text-sm text-slate-500">
              Please contact the administrator if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <Icon name="CheckCircle" size={48} className="text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Thank You</h2>
            <p className="text-slate-600">
              Your documents have been uploaded successfully. The administrator will review them shortly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { vendor, items, expires_at } = data;

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardContent className="p-6 space-y-6">
            <div className="text-center border-b border-slate-200 pb-6">
              <h1 className="text-2xl font-bold text-slate-900">Document Resubmission Request</h1>
              <p className="text-slate-600 mt-1">
                Please upload the requested documents below. Uploaded files will replace any existing versions.
              </p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-semibold text-slate-900 mb-2">Vendor Information</h3>
              <p className="text-sm text-slate-700">
                <strong>Company:</strong> {vendor.company_name}
              </p>
              <p className="text-sm text-slate-700">
                <strong>Contact:</strong> {vendor.contact_person_name}
              </p>
              <p className="text-sm text-slate-700">
                <strong>Email:</strong> {vendor.email}
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Link expires: {new Date(expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">Upload Documents</h3>
              {items.map((item) => (
                <div key={item.key} className="border border-slate-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {item.label}
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                    onChange={(e) => handleFileChange(item.key, e.target.files?.[0])}
                  />
                  {files[item.key] && (
                    <p className="text-xs text-slate-500 mt-1">
                      Selected: {files[item.key].name}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end">
              <Button
                variant="default"
                onClick={handleSubmit}
                disabled={submitting || !Object.values(files).some((f) => f && f.size > 0)}
                loading={submitting}
              >
                Submit Documents
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicDocumentRequest;
